import { useEffect, useState } from "react";
import { Plus, Trash2, Edit2, Upload, X, Star, MessageSquare, Bike, BarChart3, Wrench, Save, Calendar, Users, LogOut, Car } from "lucide-react";
import { toast } from "sonner";
import { apiClient, fileUrl, formatPrice } from "../lib/api";
import { useAuth } from "../lib/auth";

const emptyMoto = {
  brand: "", model: "", year: new Date().getFullYear(), price: "",
  condition: "sifir", mileage: "", engine_cc: "", color: "",
  description: "", images: [], is_featured: false,
};

const emptyReview = { name: "", rating: 5, text: "" };
const emptyService = { title: "", description: "", icon: "wrench", order: 0 };
const emptyCar = {
  brand: "", model: "", year: new Date().getFullYear(), price: "",
  mileage: "", fuel_type: "Benzin", transmission: "Manuel", color: "",
  description: "", images: [], is_featured: false,
};
const FUEL_TYPES = ["Benzin", "Dizel", "LPG", "Hibrit", "Elektrik"];
const TRANSMISSIONS = ["Manuel", "Otomatik", "Yarı Otomatik"];
const ICONS = ["wrench", "warehouse", "shield", "bike", "tag", "award", "settings", "truck", "badge", "credit-card", "sparkles"];

const Admin = () => {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("motorcycles");
  const [items, setItems] = useState([]);
  const [messages, setMessages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [services, setServices] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ username: "", password: "" });
  const [stats, setStats] = useState({ happy_customers: 500, motorcycles_in_stock: 120, years_experience: 15, brands_count: 30 });
  const [conditionFilter, setConditionFilter] = useState("all");

  // Modals
  const [showMotoForm, setShowMotoForm] = useState(false);
  const [motoForm, setMotoForm] = useState(emptyMoto);
  const [motoEditId, setMotoEditId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState(emptyReview);
  const [reviewEditId, setReviewEditId] = useState(null);

  const [showServiceForm, setShowServiceForm] = useState(false);
  const [serviceForm, setServiceForm] = useState(emptyService);
  const [serviceEditId, setServiceEditId] = useState(null);

  const [cars, setCars] = useState([]);
  const [showCarForm, setShowCarForm] = useState(false);
  const [carForm, setCarForm] = useState(emptyCar);
  const [carEditId, setCarEditId] = useState(null);

  const loadAll = async () => {
    const [m, msg, rv, sv, st, ap, us, cr] = await Promise.all([
      apiClient.get("/motorcycles", { params: { include_inactive: true, limit: 500 } }).then(r => r.data).catch(() => []),
      apiClient.get("/messages").then(r => r.data).catch(() => []),
      apiClient.get("/reviews", { params: { include_inactive: true } }).then(r => r.data).catch(() => []),
      apiClient.get("/services").then(r => r.data).catch(() => []),
      apiClient.get("/stats").then(r => r.data).catch(() => null),
      apiClient.get("/appointments").then(r => r.data).catch(() => []),
      user?.is_super_admin ? apiClient.get("/users").then(r => r.data).catch(() => []) : Promise.resolve([]),
      apiClient.get("/cars", { params: { include_inactive: true, limit: 500 } }).then(r => r.data).catch(() => []),
    ]);
    setItems(m); setMessages(msg); setReviews(rv); setServices(sv); setAppointments(ap); setUsers(us); setCars(cr);
    if (st) setStats(st);
  };

  useEffect(() => { loadAll(); }, []);

  const filteredItems = conditionFilter === "all" ? items : items.filter(i => i.condition === conditionFilter);

  // ---- MOTO ----
  const openMotoCreate = (cond = "sifir") => {
    setMotoForm({ ...emptyMoto, condition: cond });
    setMotoEditId(null);
    setShowMotoForm(true);
  };

  const openMotoEdit = (m) => {
    setMotoForm({ ...emptyMoto, ...m, mileage: m.mileage ?? "", engine_cc: m.engine_cc ?? "", color: m.color ?? "", description: m.description ?? "" });
    setMotoEditId(m.id);
    setShowMotoForm(true);
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    const newPaths = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await apiClient.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newPaths.push(r.data.path);
      } catch { toast.error(`Yüklenemedi: ${f.name}`); }
    }
    setMotoForm(p => ({ ...p, images: [...p.images, ...newPaths] }));
    setUploading(false);
    e.target.value = "";
    if (newPaths.length > 0) toast.success(`${newPaths.length} görsel yüklendi`);
  };

  const submitMoto = async (e) => {
    e.preventDefault();
    if (!motoForm.brand || !motoForm.model || !motoForm.year || !motoForm.price) {
      toast.error("Marka, model, yıl ve fiyat zorunludur");
      return;
    }
    const payload = {
      ...motoForm,
      year: Number(motoForm.year),
      price: Number(motoForm.price),
      mileage: motoForm.mileage === "" ? null : Number(motoForm.mileage),
      engine_cc: motoForm.engine_cc === "" ? null : Number(motoForm.engine_cc),
    };
    try {
      if (motoEditId) {
        await apiClient.put(`/motorcycles/${motoEditId}`, payload);
        toast.success("Motor güncellendi");
      } else {
        await apiClient.post("/motorcycles", payload);
        toast.success("Motor eklendi");
      }
      setShowMotoForm(false);
      loadAll();
    } catch { toast.error("İşlem başarısız"); }
  };

  const deleteMoto = async (id) => {
    if (!window.confirm("Bu motoru silmek istediğinize emin misiniz?")) return;
    try { await apiClient.delete(`/motorcycles/${id}`); toast.success("Motor silindi"); loadAll(); }
    catch { toast.error("Silinemedi"); }
  };

  // ---- STATS ----
  const saveStats = async () => {
    try {
      await apiClient.put("/stats", {
        happy_customers: Number(stats.happy_customers),
        motorcycles_in_stock: Number(stats.motorcycles_in_stock),
        years_experience: Number(stats.years_experience),
        brands_count: Number(stats.brands_count),
      });
      toast.success("İstatistikler güncellendi");
    } catch { toast.error("Kaydedilemedi"); }
  };

  // ---- REVIEWS ----
  const openReviewCreate = () => { setReviewForm(emptyReview); setReviewEditId(null); setShowReviewForm(true); };
  const openReviewEdit = (r) => { setReviewForm({ name: r.name, rating: r.rating, text: r.text }); setReviewEditId(r.id); setShowReviewForm(true); };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.name || !reviewForm.text) { toast.error("Ad ve yorum zorunludur"); return; }
    const payload = { ...reviewForm, rating: Number(reviewForm.rating) };
    try {
      if (reviewEditId) { await apiClient.put(`/reviews/${reviewEditId}`, payload); toast.success("Yorum güncellendi"); }
      else { await apiClient.post("/reviews", payload); toast.success("Yorum eklendi"); }
      setShowReviewForm(false); loadAll();
    } catch { toast.error("İşlem başarısız"); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Bu yorumu silmek istiyor musunuz?")) return;
    try { await apiClient.delete(`/reviews/${id}`); toast.success("Yorum silindi"); loadAll(); }
    catch { toast.error("Silinemedi"); }
  };

  // ---- SERVICES ----
  const openServiceCreate = () => { setServiceForm(emptyService); setServiceEditId(null); setShowServiceForm(true); };
  const openServiceEdit = (s) => { setServiceForm({ title: s.title, description: s.description, icon: s.icon, order: s.order }); setServiceEditId(s.id); setShowServiceForm(true); };

  const submitService = async (e) => {
    e.preventDefault();
    if (!serviceForm.title || !serviceForm.description) { toast.error("Başlık ve açıklama zorunludur"); return; }
    const payload = { ...serviceForm, order: Number(serviceForm.order) };
    try {
      if (serviceEditId) { await apiClient.put(`/services/${serviceEditId}`, payload); toast.success("Hizmet güncellendi"); }
      else { await apiClient.post("/services", payload); toast.success("Hizmet eklendi"); }
      setShowServiceForm(false); loadAll();
    } catch { toast.error("İşlem başarısız"); }
  };

  const deleteService = async (id) => {
    if (!window.confirm("Bu hizmeti silmek istiyor musunuz?")) return;
    try { await apiClient.delete(`/services/${id}`); toast.success("Hizmet silindi"); loadAll(); }
    catch { toast.error("Silinemedi"); }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm("Bu mesajı silmek istiyor musunuz?")) return;
    try { await apiClient.delete(`/messages/${id}`); toast.success("Mesaj silindi"); loadAll(); }
    catch { toast.error("Silinemedi"); }
  };

  const TABS = [
    { id: "motorcycles", label: `Motorlar (${items.length})`, icon: Bike, testid: "tab-motorcycles" },
    { id: "cars", label: `Arabalar (${cars.length})`, icon: Car, testid: "tab-cars" },
    { id: "appointments", label: `Randevular (${appointments.length})`, icon: Calendar, testid: "tab-appointments" },
    { id: "stats", label: "İstatistikler", icon: BarChart3, testid: "tab-stats" },
    { id: "reviews", label: `Yorumlar (${reviews.length})`, icon: Star, testid: "tab-reviews" },
    { id: "services", label: `Hizmetler (${services.length})`, icon: Wrench, testid: "tab-services" },
    { id: "messages", label: `Mesajlar (${messages.length})`, icon: MessageSquare, testid: "tab-messages" },
    ...(user?.is_super_admin ? [{ id: "users", label: `Kullanıcılar (${users.length})`, icon: Users, testid: "tab-users" }] : []),
  ];

  const updateAptStatus = async (id, status) => {
    try { await apiClient.put(`/appointments/${id}`, { status }); toast.success("Durum güncellendi"); loadAll(); }
    catch { toast.error("Güncellenemedi"); }
  };
  const deleteApt = async (id) => {
    if (!window.confirm("Bu randevuyu silmek istiyor musunuz?")) return;
    try { await apiClient.delete(`/appointments/${id}`); toast.success("Randevu silindi"); loadAll(); }
    catch { toast.error("Silinemedi"); }
  };
  const createNewUser = async (e) => {
    e.preventDefault();
    if (!newUser.username || !newUser.password) { toast.error("Kullanıcı adı ve şifre gerekli"); return; }
    try {
      await apiClient.post("/users", newUser);
      toast.success("Kullanıcı eklendi");
      setNewUser({ username: "", password: "" });
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Kullanıcı eklenemedi"); }
  };
  const deleteUser = async (id) => {
    if (!window.confirm("Bu kullanıcıyı silmek istiyor musunuz?")) return;
    try { await apiClient.delete(`/users/${id}`); toast.success("Kullanıcı silindi"); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || "Silinemedi"); }
  };

  // ---- CARS ----
  const openCarCreate = () => { setCarForm(emptyCar); setCarEditId(null); setShowCarForm(true); };
  const openCarEdit = (c) => {
    setCarForm({ ...emptyCar, ...c, mileage: c.mileage ?? "", color: c.color ?? "", description: c.description ?? "", fuel_type: c.fuel_type ?? "Benzin", transmission: c.transmission ?? "Manuel" });
    setCarEditId(c.id);
    setShowCarForm(true);
  };
  const handleCarUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    const newPaths = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await apiClient.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newPaths.push(r.data.path);
      } catch { toast.error(`Yüklenemedi: ${f.name}`); }
    }
    setCarForm(p => ({ ...p, images: [...p.images, ...newPaths] }));
    setUploading(false);
    e.target.value = "";
    if (newPaths.length > 0) toast.success(`${newPaths.length} görsel yüklendi`);
  };
  const submitCar = async (e) => {
    e.preventDefault();
    if (!carForm.brand || !carForm.model || !carForm.year || !carForm.price) {
      toast.error("Marka, model, yıl ve fiyat zorunludur");
      return;
    }
    const payload = {
      ...carForm,
      year: Number(carForm.year),
      price: Number(carForm.price),
      mileage: carForm.mileage === "" ? null : Number(carForm.mileage),
    };
    try {
      if (carEditId) { await apiClient.put(`/cars/${carEditId}`, payload); toast.success("Araba güncellendi"); }
      else { await apiClient.post("/cars", payload); toast.success("Araba eklendi"); }
      setShowCarForm(false); loadAll();
    } catch { toast.error("İşlem başarısız"); }
  };
  const deleteCar = async (id) => {
    if (!window.confirm("Bu arabayı silmek istediğinize emin misiniz?")) return;
    try { await apiClient.delete(`/cars/${id}`); toast.success("Araba silindi"); loadAll(); }
    catch { toast.error("Silinemedi"); }
  };

  return (
    <div data-testid="admin-page" className="bg-zinc-950 text-white pt-28 pb-24 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="mb-10 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="block h-px w-12 bg-orange-500" />
              <span className="text-orange-500 font-bold uppercase tracking-[0.3em] text-xs">Yönetim Paneli</span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold uppercase leading-none tracking-tight">
              Gensan <span className="text-orange-500">Admin</span>
            </h1>
            {user && <p className="mt-2 text-xs uppercase tracking-widest text-zinc-500">Giriş: {user.username}{user.is_super_admin ? " (Süper Admin)" : ""}</p>}
          </div>
          <button onClick={logout} data-testid="logout-button"
            className="inline-flex items-center gap-2 border border-zinc-800 hover:border-red-500 hover:text-red-500 text-zinc-400 px-5 py-3 font-bold uppercase tracking-widest text-xs transition-colors">
            <LogOut size={14} /> Çıkış
          </button>
        </div>

        <div className="flex gap-1 mb-8 border-b border-zinc-900 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} data-testid={t.testid} onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-xs font-bold uppercase tracking-widest flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                tab === t.id ? "border-orange-500 text-orange-500" : "border-transparent text-zinc-400 hover:text-white"
              }`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "motorcycles" && (
          <>
            <div className="flex flex-wrap gap-3 items-center justify-between mb-6">
              <div className="flex gap-2">
                {["all", "sifir", "ikinci-el"].map(c => (
                  <button key={c} onClick={() => setConditionFilter(c)}
                    className={`px-4 py-2 text-xs uppercase tracking-widest border ${
                      conditionFilter === c ? "border-orange-500 text-orange-500" : "border-zinc-800 text-zinc-400 hover:text-white"
                    }`}>
                    {c === "all" ? "Tümü" : c === "sifir" ? "Sıfır" : "İkinci El"}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={() => openMotoCreate("sifir")} data-testid="add-sifir-button"
                  className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-3 font-bold uppercase tracking-widest text-xs">
                  <Plus size={14} /> Sıfır Motor
                </button>
                <button onClick={() => openMotoCreate("ikinci-el")} data-testid="add-ikinci-el-button"
                  className="inline-flex items-center gap-2 bg-white hover:bg-zinc-200 text-black px-5 py-3 font-bold uppercase tracking-widest text-xs">
                  <Plus size={14} /> İkinci El
                </button>
              </div>
            </div>

            {filteredItems.length === 0 ? (
              <div className="border border-zinc-900 p-20 text-center">
                <p className="text-zinc-400 uppercase tracking-widest text-sm">Bu kategoride motor yok</p>
              </div>
            ) : (
              <div className="border border-zinc-900 divide-y divide-zinc-900">
                {filteredItems.map(m => (
                  <div key={m.id} className="flex items-center gap-4 p-4 hover:bg-black/40 transition-colors">
                    <div className="w-20 h-16 bg-zinc-900 shrink-0 overflow-hidden">
                      {m.images?.[0] && <img src={fileUrl(m.images[0])} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${m.condition === "sifir" ? "bg-orange-600 text-white" : "bg-white text-black"}`}>
                          {m.condition === "sifir" ? "Sıfır" : "2. El"}
                        </span>
                        {m.is_featured && <Star size={12} className="text-orange-500 fill-orange-500" />}
                      </div>
                      <div className="font-display text-lg font-bold uppercase mt-1 truncate">{m.brand} {m.model}</div>
                      <div className="text-xs text-zinc-500">{m.year} • {formatPrice(m.price)}</div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => openMotoEdit(m)} data-testid={`edit-${m.id}`} className="p-2 border border-zinc-800 hover:border-orange-500 hover:text-orange-500 text-zinc-400 transition-colors"><Edit2 size={16} /></button>
                      <button onClick={() => deleteMoto(m.id)} data-testid={`delete-${m.id}`} className="p-2 border border-zinc-800 hover:border-red-500 hover:text-red-500 text-zinc-400 transition-colors"><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "cars" && (
          <>
            <div className="flex justify-end mb-6">
              <button onClick={openCarCreate} data-testid="add-car-button"
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-3 font-bold uppercase tracking-widest text-xs">
                <Plus size={14} /> Araba Ekle
              </button>
            </div>
            {cars.length === 0 ? (
              <div className="border border-zinc-900 p-20 text-center">
                <p className="text-zinc-400 uppercase tracking-widest text-sm">Henüz araba eklenmedi</p>
              </div>
            ) : (
              <div className="border border-zinc-900 divide-y divide-zinc-900">
                {cars.map(c => (
                  <div key={c.id} className="flex items-center gap-4 p-4 hover:bg-black/40 transition-colors">
                    <div className="w-20 h-16 bg-zinc-900 shrink-0 overflow-hidden">
                      {c.images?.[0] && <img src={fileUrl(c.images[0])} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-white text-black">2. El Araç</span>
                        {c.is_featured && <Star size={12} className="text-orange-500 fill-orange-500" />}
                      </div>
                      <div className="font-display text-lg font-bold uppercase mt-1 truncate">{c.brand} {c.model}</div>
                      <div className="text-xs text-zinc-500">{c.year} • {formatPrice(c.price)}{c.fuel_type ? ` • ${c.fuel_type}` : ""}{c.transmission ? ` • ${c.transmission}` : ""}</div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => openCarEdit(c)} data-testid={`edit-car-${c.id}`} className="p-2 border border-zinc-800 hover:border-orange-500 hover:text-orange-500 text-zinc-400 transition-colors"><Edit2 size={16} /></button>
                      <button onClick={() => deleteCar(c.id)} data-testid={`delete-car-${c.id}`} className="p-2 border border-zinc-800 hover:border-red-500 hover:text-red-500 text-zinc-400 transition-colors"><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}


        {tab === "appointments" && (
          <>
            {appointments.length === 0 ? (
              <div className="border border-zinc-900 p-20 text-center">
                <p className="text-zinc-400 uppercase tracking-widest text-sm">Henüz randevu talebi yok</p>
              </div>
            ) : (
              <div className="border border-zinc-900 divide-y divide-zinc-900">
                {appointments.map(a => (
                  <div key={a.id} className="flex items-center gap-4 p-4 hover:bg-black/40 transition-colors flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-lg font-bold uppercase">{a.name}</div>
                      <div className="text-sm text-zinc-400 space-y-0.5 mt-1">
                        <div><span className="text-zinc-500 uppercase text-[10px] tracking-widest">Tel:</span> {a.phone}</div>
                        {(a.motorcycle_brand || a.motorcycle_model) && <div><span className="text-zinc-500 uppercase text-[10px] tracking-widest">Motor:</span> {a.motorcycle_brand} {a.motorcycle_model}</div>}
                        {a.service_type && <div><span className="text-zinc-500 uppercase text-[10px] tracking-widest">Hizmet:</span> {a.service_type}</div>}
                        {a.appointment_date && <div><span className="text-zinc-500 uppercase text-[10px] tracking-widest">Tarih:</span> {a.appointment_date}</div>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select data-testid={`apt-status-${a.id}`} value={a.status || "beklemede"} onChange={e => updateAptStatus(a.id, e.target.value)}
                        className="bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-3 py-2 text-xs text-white uppercase tracking-widest">
                        <option value="beklemede" className="bg-black">Beklemede</option>
                        <option value="onaylandi" className="bg-black">Onaylandı</option>
                        <option value="tamamlandi" className="bg-black">Tamamlandı</option>
                        <option value="iptal" className="bg-black">İptal</option>
                      </select>
                      <button onClick={() => deleteApt(a.id)} data-testid={`apt-delete-${a.id}`} className="p-2 border border-zinc-800 hover:border-red-500 hover:text-red-500 text-zinc-400 transition-colors"><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "stats" && (
          <div className="max-w-2xl">
            <p className="text-zinc-400 mb-8">Ana sayfa ve hakkımızda sayfasındaki istatistikleri buradan düzenleyin.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <StatInput label="Mutlu Müşteri" testid="stat-customers" value={stats.happy_customers} onChange={v => setStats({...stats, happy_customers: v})} />
              <StatInput label="Stoktaki Motor" testid="stat-stock" value={stats.motorcycles_in_stock} onChange={v => setStats({...stats, motorcycles_in_stock: v})} />
              <StatInput label="Yıllık Tecrübe" testid="stat-years" value={stats.years_experience} onChange={v => setStats({...stats, years_experience: v})} />
              <StatInput label="Marka Sayısı" testid="stat-brands" value={stats.brands_count} onChange={v => setStats({...stats, brands_count: v})} />
            </div>
            <button onClick={saveStats} data-testid="stats-save-button"
              className="mt-8 inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 font-bold uppercase tracking-widest text-xs">
              <Save size={14} /> Kaydet
            </button>
          </div>
        )}

        {tab === "reviews" && (
          <>
            <div className="flex justify-end mb-6">
              <button onClick={openReviewCreate} data-testid="add-review-button"
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-3 font-bold uppercase tracking-widest text-xs">
                <Plus size={14} /> Yorum Ekle
              </button>
            </div>
            {reviews.length === 0 ? (
              <div className="border border-zinc-900 p-20 text-center">
                <p className="text-zinc-400 uppercase tracking-widest text-sm">Henüz yorum eklenmedi</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {reviews.map(r => (
                  <div key={r.id} className="bg-black border border-zinc-900 p-5">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="font-display text-lg font-bold">{r.name}</div>
                        <div className="flex gap-0.5 mt-1">
                          {Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={12} className="fill-orange-500 text-orange-500" />)}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => openReviewEdit(r)} data-testid={`edit-review-${r.id}`} className="p-1.5 text-zinc-500 hover:text-orange-500"><Edit2 size={14} /></button>
                        <button onClick={() => deleteReview(r.id)} data-testid={`delete-review-${r.id}`} className="p-1.5 text-zinc-500 hover:text-red-500"><Trash2 size={14} /></button>
                      </div>
                    </div>
                    <p className="text-zinc-300 text-sm leading-relaxed italic">"{r.text}"</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "services" && (
          <>
            <div className="flex justify-end mb-6">
              <button onClick={openServiceCreate} data-testid="add-service-button"
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-3 font-bold uppercase tracking-widest text-xs">
                <Plus size={14} /> Hizmet Ekle
              </button>
            </div>
            {services.length === 0 ? (
              <div className="border border-zinc-900 p-20 text-center">
                <p className="text-zinc-400 uppercase tracking-widest text-sm">Henüz hizmet eklenmedi. (Boş bırakılırsa ana sayfada varsayılan 3 özellik gösterilir.)</p>
              </div>
            ) : (
              <div className="border border-zinc-900 divide-y divide-zinc-900">
                {services.map(s => (
                  <div key={s.id} className="flex items-center gap-4 p-4 hover:bg-black/40">
                    <div className="w-12 h-12 bg-orange-600 flex items-center justify-center text-white text-xs uppercase">
                      {s.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-lg font-bold uppercase">{s.title}</div>
                      <div className="text-sm text-zinc-400 truncate">{s.description}</div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => openServiceEdit(s)} data-testid={`edit-service-${s.id}`} className="p-2 border border-zinc-800 hover:border-orange-500 hover:text-orange-500 text-zinc-400"><Edit2 size={16} /></button>
                      <button onClick={() => deleteService(s.id)} data-testid={`delete-service-${s.id}`} className="p-2 border border-zinc-800 hover:border-red-500 hover:text-red-500 text-zinc-400"><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "messages" && (
          <>
            {messages.length === 0 ? (
              <div className="border border-zinc-900 p-20 text-center">
                <p className="text-zinc-400 uppercase tracking-widest text-sm">Henüz mesaj alınmadı</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {messages.map(m => (
                  <div key={m.id} className="bg-black border border-zinc-900 p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="font-display text-lg font-bold">{m.name}</div>
                        <div className="text-xs text-zinc-500">{new Date(m.created_at).toLocaleString("tr-TR")}</div>
                      </div>
                      <button onClick={() => deleteMessage(m.id)} data-testid={`msg-delete-${m.id}`} className="p-2 text-zinc-500 hover:text-red-500"><Trash2 size={14} /></button>
                    </div>
                    <div className="text-sm text-zinc-300 space-y-1 mb-3">
                      <div><span className="text-zinc-500 uppercase text-[10px] tracking-widest">Tel:</span> {m.phone}</div>
                      {m.email && <div><span className="text-zinc-500 uppercase text-[10px] tracking-widest">Email:</span> {m.email}</div>}
                      {m.subject && <div><span className="text-zinc-500 uppercase text-[10px] tracking-widest">Konu:</span> {m.subject}</div>}
                    </div>
                    <p className="text-zinc-300 text-sm leading-relaxed border-t border-zinc-900 pt-3">{m.message}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "users" && user?.is_super_admin && (
          <>
            <form onSubmit={createNewUser} className="flex flex-wrap gap-3 items-end mb-8 border border-zinc-900 p-5">
              <div className="flex-1 min-w-[160px]">
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Kullanıcı Adı</label>
                <input data-testid="new-user-username" value={newUser.username} onChange={e => setNewUser({...newUser, username: e.target.value})}
                  className="w-full bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-white" />
              </div>
              <div className="flex-1 min-w-[160px]">
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Şifre</label>
                <input data-testid="new-user-password" type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})}
                  className="w-full bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-white" />
              </div>
              <button type="submit" data-testid="add-user-button"
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-3 font-bold uppercase tracking-widest text-xs">
                <Plus size={14} /> Kullanıcı Ekle
              </button>
            </form>
            <div className="border border-zinc-900 divide-y divide-zinc-900">
              {users.map(u => (
                <div key={u.id} className="flex items-center gap-4 p-4 hover:bg-black/40">
                  <Users size={18} className="text-zinc-500" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg font-bold uppercase">{u.username}</div>
                    {u.is_super_admin && <div className="text-xs text-orange-500 uppercase tracking-widest">Süper Admin</div>}
                  </div>
                  {!u.is_super_admin && (
                    <button onClick={() => deleteUser(u.id)} data-testid={`user-delete-${u.id}`} className="p-2 border border-zinc-800 hover:border-red-500 hover:text-red-500 text-zinc-400 transition-colors"><Trash2 size={16} /></button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* MOTO MODAL */}
      {showMotoForm && (
        <Modal onClose={() => setShowMotoForm(false)} title={motoEditId ? "Motor Düzenle" : "Yeni Motor"}>
          <form onSubmit={submitMoto} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Marka *" testid="form-brand" value={motoForm.brand} onChange={v => setMotoForm({...motoForm, brand: v})} />
              <Field label="Model *" testid="form-model" value={motoForm.model} onChange={v => setMotoForm({...motoForm, model: v})} />
              <Field label="Yıl *" testid="form-year" type="number" value={motoForm.year} onChange={v => setMotoForm({...motoForm, year: v})} />
              <Field label="Fiyat (₺) *" testid="form-price" type="number" value={motoForm.price} onChange={v => setMotoForm({...motoForm, price: v})} />
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Durum</label>
                <select data-testid="form-condition" value={motoForm.condition} onChange={e => setMotoForm({...motoForm, condition: e.target.value})}
                  className="w-full bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-white">
                  <option value="sifir" className="bg-black">Sıfır</option>
                  <option value="ikinci-el" className="bg-black">İkinci El</option>
                </select>
              </div>
              <Field label="KM" testid="form-mileage" type="number" value={motoForm.mileage} onChange={v => setMotoForm({...motoForm, mileage: v})} />
              <Field label="Motor (cc)" testid="form-engine" type="number" value={motoForm.engine_cc} onChange={v => setMotoForm({...motoForm, engine_cc: v})} />
              <Field label="Renk" testid="form-color" value={motoForm.color} onChange={v => setMotoForm({...motoForm, color: v})} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Açıklama</label>
              <textarea data-testid="form-description" value={motoForm.description} onChange={e => setMotoForm({...motoForm, description: e.target.value})} rows={4}
                className="w-full bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-white resize-none" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-3">Görseller</label>
              <div className="grid grid-cols-4 gap-3 mb-3">
                {motoForm.images.map((img, i) => (
                  <div key={i} className="relative aspect-square bg-zinc-900 border border-zinc-800">
                    <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setMotoForm(p => ({...p, images: p.images.filter((_, k) => k !== i)}))}
                      className="absolute top-1 right-1 bg-black/80 text-white p-1 hover:bg-red-600"><X size={12} /></button>
                  </div>
                ))}
                <label className="aspect-square border-2 border-dashed border-zinc-800 hover:border-orange-500 flex flex-col items-center justify-center cursor-pointer text-zinc-500 hover:text-orange-500 transition-colors">
                  <Upload size={20} />
                  <span className="text-[10px] uppercase tracking-widest mt-1">{uploading ? "Yükleniyor..." : "Görsel Ekle"}</span>
                  <input data-testid="form-upload" type="file" multiple accept="image/*" onChange={handleUpload} disabled={uploading} className="hidden" />
                </label>
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input data-testid="form-featured" type="checkbox" checked={motoForm.is_featured} onChange={e => setMotoForm({...motoForm, is_featured: e.target.checked})} className="w-5 h-5 accent-orange-500" />
              <span className="text-sm uppercase tracking-widest">Ana sayfada öne çıkar</span>
            </label>
            <FormActions onCancel={() => setShowMotoForm(false)} submitLabel={motoEditId ? "Güncelle" : "Kaydet"} />
          </form>
        </Modal>
      )}

      {/* CAR MODAL */}
      {showCarForm && (
        <Modal onClose={() => setShowCarForm(false)} title={carEditId ? "Araba Düzenle" : "Yeni Araba"}>
          <form onSubmit={submitCar} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Marka *" testid="car-brand" value={carForm.brand} onChange={v => setCarForm({...carForm, brand: v})} />
              <Field label="Model *" testid="car-model" value={carForm.model} onChange={v => setCarForm({...carForm, model: v})} />
              <Field label="Yıl *" testid="car-year" type="number" value={carForm.year} onChange={v => setCarForm({...carForm, year: v})} />
              <Field label="Fiyat (₺) *" testid="car-price" type="number" value={carForm.price} onChange={v => setCarForm({...carForm, price: v})} />
              <Field label="KM" testid="car-mileage" type="number" value={carForm.mileage} onChange={v => setCarForm({...carForm, mileage: v})} />
              <Field label="Renk" testid="car-color" value={carForm.color} onChange={v => setCarForm({...carForm, color: v})} />
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Yakıt Tipi</label>
                <select data-testid="car-fuel" value={carForm.fuel_type} onChange={e => setCarForm({...carForm, fuel_type: e.target.value})}
                  className="w-full bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-white">
                  {FUEL_TYPES.map(f => <option key={f} value={f} className="bg-black">{f}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Vites Tipi</label>
                <select data-testid="car-transmission" value={carForm.transmission} onChange={e => setCarForm({...carForm, transmission: e.target.value})}
                  className="w-full bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-white">
                  {TRANSMISSIONS.map(t => <option key={t} value={t} className="bg-black">{t}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Açıklama</label>
              <textarea data-testid="car-description" value={carForm.description} onChange={e => setCarForm({...carForm, description: e.target.value})} rows={4}
                className="w-full bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-white resize-none" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-3">Görseller</label>
              <div className="grid grid-cols-4 gap-3 mb-3">
                {carForm.images.map((img, i) => (
                  <div key={i} className="relative aspect-square bg-zinc-900 border border-zinc-800">
                    <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setCarForm(p => ({...p, images: p.images.filter((_, k) => k !== i)}))}
                      className="absolute top-1 right-1 bg-black/80 text-white p-1 hover:bg-red-600"><X size={12} /></button>
                  </div>
                ))}
                <label className="aspect-square border-2 border-dashed border-zinc-800 hover:border-orange-500 flex flex-col items-center justify-center cursor-pointer text-zinc-500 hover:text-orange-500 transition-colors">
                  <Upload size={20} />
                  <span className="text-[10px] uppercase tracking-widest mt-1">{uploading ? "Yükleniyor..." : "Görsel Ekle"}</span>
                  <input data-testid="car-upload" type="file" multiple accept="image/*" onChange={handleCarUpload} disabled={uploading} className="hidden" />
                </label>
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input data-testid="car-featured" type="checkbox" checked={carForm.is_featured} onChange={e => setCarForm({...carForm, is_featured: e.target.checked})} className="w-5 h-5 accent-orange-500" />
              <span className="text-sm uppercase tracking-widest">Öne çıkar</span>
            </label>
            <FormActions onCancel={() => setShowCarForm(false)} submitLabel={carEditId ? "Güncelle" : "Kaydet"} />
          </form>
        </Modal>
      )}


      {/* REVIEW MODAL */}
      {showReviewForm && (
        <Modal onClose={() => setShowReviewForm(false)} title={reviewEditId ? "Yorum Düzenle" : "Yeni Yorum"}>
          <form onSubmit={submitReview} className="space-y-5">
            <Field label="Müşteri Adı *" testid="review-name" value={reviewForm.name} onChange={v => setReviewForm({...reviewForm, name: v})} />
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Puan</label>
              <select data-testid="review-rating" value={reviewForm.rating} onChange={e => setReviewForm({...reviewForm, rating: Number(e.target.value)})}
                className="w-full bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-white">
                {[5,4,3,2,1].map(n => <option key={n} value={n} className="bg-black">{n} Yıldız</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Yorum *</label>
              <textarea data-testid="review-text" value={reviewForm.text} onChange={e => setReviewForm({...reviewForm, text: e.target.value})} rows={4}
                className="w-full bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-white resize-none" />
            </div>
            <FormActions onCancel={() => setShowReviewForm(false)} submitLabel={reviewEditId ? "Güncelle" : "Kaydet"} />
          </form>
        </Modal>
      )}

      {/* SERVICE MODAL */}
      {showServiceForm && (
        <Modal onClose={() => setShowServiceForm(false)} title={serviceEditId ? "Hizmet Düzenle" : "Yeni Hizmet"}>
          <form onSubmit={submitService} className="space-y-5">
            <Field label="Başlık *" testid="service-title" value={serviceForm.title} onChange={v => setServiceForm({...serviceForm, title: v})} />
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Açıklama *</label>
              <textarea data-testid="service-description" value={serviceForm.description} onChange={e => setServiceForm({...serviceForm, description: e.target.value})} rows={3}
                className="w-full bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-white resize-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">İkon</label>
                <select data-testid="service-icon" value={serviceForm.icon} onChange={e => setServiceForm({...serviceForm, icon: e.target.value})}
                  className="w-full bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-white">
                  {ICONS.map(i => <option key={i} value={i} className="bg-black">{i}</option>)}
                </select>
              </div>
              <Field label="Sıralama" testid="service-order" type="number" value={serviceForm.order} onChange={v => setServiceForm({...serviceForm, order: v})} />
            </div>
            <FormActions onCancel={() => setShowServiceForm(false)} submitLabel={serviceEditId ? "Güncelle" : "Kaydet"} />
          </form>
        </Modal>
      )}
    </div>
  );
};

const StatInput = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">{label}</label>
    <input data-testid={testid} type="number" value={value} onChange={e => onChange(e.target.value)}
      className="w-full bg-zinc-950 border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-2xl font-display font-bold text-white" />
  </div>
);

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">{label}</label>
    <input data-testid={testid} type={type} value={value} onChange={e => onChange(e.target.value)}
      className="w-full bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-white transition-colors" />
  </div>
);

const Modal = ({ children, onClose, title }) => (
  <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
    <div className="bg-zinc-950 border border-zinc-800 max-w-2xl w-full my-8">
      <div className="flex items-center justify-between p-6 border-b border-zinc-900">
        <h3 className="font-display text-2xl font-bold uppercase">{title}</h3>
        <button onClick={onClose} data-testid="close-form" className="text-zinc-400 hover:text-white"><X size={22} /></button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

const FormActions = ({ onCancel, submitLabel }) => (
  <div className="flex gap-3 pt-4 border-t border-zinc-900">
    <button type="button" onClick={onCancel}
      className="flex-1 border border-zinc-800 hover:border-white text-white px-6 py-3 font-bold uppercase tracking-widest text-sm transition-colors">İptal</button>
    <button type="submit" data-testid="form-submit"
      className="flex-1 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 font-bold uppercase tracking-widest text-sm transition-colors">{submitLabel}</button>
  </div>
);

export default Admin;
